import { skipToken } from '@reduxjs/toolkit/query';
import { useFetchActiveOnPageSeoAnalysisProcessesQuery } from '../service';
import { useEffect, useState } from 'react';
import { DOMAIN_ANALYSIS_PROCESS_POLLING_TIME_MS } from '../../settings';
import { deepAnalysisApi } from '../../service';
import { useAppDispatch } from '../../../useAppDispatch';

/**
 * A hook that tracks the status of the current domain analysis process.
 * @param websiteGuid
 * @param shouldRefetchOnMount whether the RTK hook should force a refetch on mounts. defaults to **True**
 * @returns A list of ongoing domain analysis processes and associated data
 */
export default function useDomainAnalysisProcessPolling(websiteGuid: string | null, shouldRefetchOnMount: boolean = true) {
    const dispatch = useAppDispatch();
    const [isAnalysisProcessActive, setIsAnalysisProcessActive] = useState<boolean>(false);

    const { data: runningDomainAnalysisProcesses, isLoading: isLoadingDomainAnalysisProcesses } = useFetchActiveOnPageSeoAnalysisProcessesQuery(
        websiteGuid ? { w_guid: websiteGuid, process_type: 'on_page_seo_domain_analysis' } : skipToken,
        {
            pollingInterval: isAnalysisProcessActive ? DOMAIN_ANALYSIS_PROCESS_POLLING_TIME_MS : 0,
            // If this is set to a number or true it will force refetch on component mount
            refetchOnMountOrArgChange: shouldRefetchOnMount,
        }
    );

    const hasDomainAnalysisProcesses = !!runningDomainAnalysisProcesses && runningDomainAnalysisProcesses.length > 0;
    const hasNoDomainAnalysisProcesses = !!runningDomainAnalysisProcesses && runningDomainAnalysisProcesses.length === 0;

    useEffect(() => {
        // Had no batch process but now there are
        if (hasDomainAnalysisProcesses && isAnalysisProcessActive === false) {
            setIsAnalysisProcessActive(true);
        }

        // Had batch processes but they are finished
        if (hasNoDomainAnalysisProcesses && isAnalysisProcessActive === true) {
            setIsAnalysisProcessActive(false);

            if (websiteGuid) {
                dispatch(
                    deepAnalysisApi.util.invalidateTags([
                        { type: 'Favicons' }
                    ])
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, hasNoDomainAnalysisProcesses, hasDomainAnalysisProcesses, websiteGuid]);

    const isRunningDomainAnalysis = !!runningDomainAnalysisProcesses && runningDomainAnalysisProcesses.length > 0;

    return {
        activeBatchUrlAnalysisProcess: runningDomainAnalysisProcesses ? runningDomainAnalysisProcesses[0] : null,
        isRunningDomainAnalysis,
        isLoadingDomainAnalysisProcesses,
    };
}
